import { Link } from "react-router-dom";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import Container from "@/components/base/Container";
import ContactCards from "@/components/feature/ContactCards";
import ContactForm from "@/components/feature/ContactForm";
import Footer from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import SubscribeForm from "@/components/feature/SubscribeForm";
import useSeo from "@/hooks/useSeo";
import { faqs } from "@/mocks/faqs";

const BASE = "https://iosfxw.cn";

export default function Contact() {
  useSeo({
    title: "联系我们｜内容更正·线索投稿·转载授权 - 瓜田速报",
    description:
      "通过表单或邮件联系瓜田速报，处理内容更正、事实核对、线索投稿与转载授权等事项。我们会在两个工作日内回复，涉及事实偏差的内容会核实后及时更正。",
    keywords: "瓜田速报联系方式,内容更正,线索投稿,转载授权,五一盘点反馈",
    canonical: `${BASE}/contact`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系瓜田速报",
        url: `${BASE}/contact`,
        inLanguage: "zh-CN",
        description: "瓜田速报的内容更正、线索投稿与转载授权联系方式。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${BASE}/` },
          { "@type": "ListItem", position: 2, name: "联系我们", item: `${BASE}/contact` },
        ],
      },
    ],
  });

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-[74px]">
        <section className="border-b border-background-200 bg-background-100 py-10 md:py-14">
          <Container>
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
            <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-[40px]">
              联系我们
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              不管你是想指出一处事实偏差、提供一条线索，还是沟通转载与内容合作，都可以通过下面的方式找到我们。为了更快处理，请尽量附上公开链接与时间点。
            </p>
          </Container>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <Container>
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-5">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">先选对渠道</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  不同的诉求走不同入口，处理速度会更快。紧急的事实更正建议在表单里直接说明，并留下可回拨的联系方式。
                </p>
                <div className="mt-6">
                  <ContactCards />
                </div>

                <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
                  <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-time-line text-accent-600" />
                    回复时间说明
                  </h3>
                  <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-foreground-600">
                    <li className="flex items-start gap-2.5">
                      <i className="ri-checkbox-blank-circle-fill mt-2 text-[6px] text-primary-500" />
                      内容更正与事实核对：通常两个工作日内回复。
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="ri-checkbox-blank-circle-fill mt-2 text-[6px] text-primary-500" />
                      线索投稿：我们会先核对公开出处，再决定是否跟进。
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="ri-checkbox-blank-circle-fill mt-2 text-[6px] text-primary-500" />
                      商业合作：三个工作日内回复，节假日可能顺延。
                    </li>
                  </ul>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">给我们留言</h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    表单内容仅用于沟通回复。带 * 的字段为必填项，详细说明请控制在 500 字以内。
                  </p>
                  <div className="mt-6">
                    <ContactForm />
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section id="subscribe" className="border-y border-background-200 bg-background-100 py-12 md:py-16">
          <Container>
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center lg:gap-12">
              <div className="lg:col-span-5">
                <span className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-background-50 px-3 py-1 text-xs font-medium text-accent-800">
                  <i className="ri-notification-3-line" />
                  订阅速报
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[30px]">
                  不想每天刷，也能不错过重点
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  留下邮箱，我们把新增事件、后续进展与澄清说明整理成一封三分钟能读完的邮件，假期结束后仍会继续更新一段时间。
                </p>
              </div>
              <div className="lg:col-span-7">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <SubscribeForm />
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="bg-background-50 py-12 md:py-16">
          <Container>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  联系之前，可以先看这几条
                </h2>
                <p className="mt-2 text-sm text-foreground-600">高频问题在这里基本都能找到答案。</p>
              </div>
              <Link
                to="/"
                className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                查看完整问答
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
              {faqs.slice(0, 3).map((faq) => (
                <div key={faq.question} className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h3 className="font-heading text-[15px] font-bold leading-snug text-foreground-950">
                    {faq.question}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </Container>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}